import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getRequest } from "../../../common/client";
import { COMPANY_EXTENDED_ENDPOINT } from "../../../common/constants";
import { formatDate, formatDate2 } from "../../../common/utils";
import HtmlHeader from "../../../components/html/html-header";
import { AbstractPage } from "../../abstractPage";
import "./index.css";

export const CompanyInfoPage = () => {
  const { companyId } = useParams();
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const url = COMPANY_EXTENDED_ENDPOINT.replace("{companyId}", companyId);
      const { ok, data } = await getRequest(url, false);

      if (ok) {
        setCompanyData(data);
      } else {
        setError("Не удалось загрузить информацию о компании");
      }
      setLoading(false);
    })();
  }, [companyId]);

  useEffect(() => {
    if (companyData?.name) {
      document.title = `${companyData.name} | Devpulse.by`;
    }
  }, [companyData]);

  if (loading) {
    return (
      <AbstractPage>
        <div className="company-info__loading">Загрузка...</div>
      </AbstractPage>
    );
  }

  if (error) {
    return (
      <AbstractPage>
        <div className="company-info__error">{error}</div>
      </AbstractPage>
    );
  }

  // Merge both datasets by date
  const mergeChartData = () => {
    const worldData = companyData?.sizeHistoryWorld || [];
    const byData = companyData?.sizeHistoryBy || [];

    const dataMap = new Map();

    worldData.forEach((record) => {
      dataMap.set(record.date, {
        date: record.date,
        world: Number(record.value),
      });
    });

    byData.forEach((record) => {
      const existing = dataMap.get(record.date) || { date: record.date };
      dataMap.set(record.date, {
        ...existing,
        by: Number(record.value),
      });
    });

    return Array.from(dataMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  };

  const chartData = mergeChartData();
  const hasChartData = chartData.length > 0;

  return (
    <AbstractPage>
      <nav className="company-info__breadcrumb">
        <Link to="/biggest-it-companies-by-employees-in-belarus">
          IT компании Беларуси
        </Link>
        <span className="company-info__breadcrumb-separator">›</span>
        <span className="company-info__breadcrumb-current">
          {companyData?.name}
        </span>
      </nav>
      <HtmlHeader text={companyData?.name} type="h1" withMargin={true} />

      {hasChartData && (
        <div className="company-info__chart-section">
          <h2 className="company-info__section-title">
            История изменения количества сотрудников
          </h2>
          <div className="company-info__chart-container">
            <ResponsiveContainer width="100%">
              <LineChart
                data={chartData}
                margin={{ top: 15, right: 10, bottom: 0, left: 10 }}
              >
                <CartesianGrid stroke="#ccc" strokeDasharray="1 4" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  style={{ fontSize: "0.7rem" }}
                  tickFormatter={(val) => formatDate2(val)}
                />
                <YAxis hide={true} domain={["auto", "auto"]} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="company-info__chart-tooltip">
                          <div className="company-info__chart-tooltip-date">
                            {formatDate(payload[0].payload.date)}
                          </div>
                          {payload[0].payload.world !== undefined && (
                            <div className="company-info__chart-tooltip-world">
                              В мире: {payload[0].payload.world}
                            </div>
                          )}
                          {payload[0].payload.by !== undefined && (
                            <div className="company-info__chart-tooltip-by">
                              В РБ: {payload[0].payload.by}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="world"
                  name="В мире"
                  stroke="#6366f1"
                  dot={false}
                  strokeWidth={1}
                />
                <Line
                  type="monotone"
                  dataKey="by"
                  name="В РБ"
                  stroke="#10b981"
                  dot={false}
                  strokeWidth={1}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {companyData?.courses && companyData.courses.length > 0 ? (
        <div className="company-info__courses-section">
          <h2 className="company-info__section-title">
            Курсы и стажировки компании
          </h2>
          <div className="company-info__courses-list">
            {companyData.courses.map((course) => (
              <div key={course.id} className="company-info__course-card">
                <div className="company-info__course-title">
                  {course.registrationOpen && <span>🔥 </span>}
                  {course.title}
                </div>
                {course.shortDescription && (
                  <div className="company-info__course-description">
                    {course.shortDescription}
                  </div>
                )}
                {course.link && (
                  <a
                    href={course.link + "?source=devpulse.by"}
                    className="company-info__course-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Подробнее →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="company-info__courses-section">
          <h2 className="company-info__section-title">
            Курсы и стажировки компании
          </h2>
          <div className="company-info__courses-list">
            У компании пока нет опубликованных курсов или стажировок
          </div>
        </div>
      )}
    </AbstractPage>
  );
};

export default CompanyInfoPage;
